import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import * as customersApi from '../api/customers';
import { getApiErrorMessage } from '../utils/errorHandling';
import {
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerStatistics,
  CustomerStatus,
  CustomerType,
} from '../api/customers';

interface UseCustomersReturn {
  customers: Customer[];
  statistics: CustomerStatistics | null;
  loading: boolean;
  error: string | null;
  createCustomer: (data: CreateCustomerDto) => Promise<Customer | null>;
  updateCustomer: (id: string, data: UpdateCustomerDto) => Promise<Customer | null>;
  deleteCustomer: (id: string) => Promise<boolean>;
  updateCustomerStatus: (id: string, status: CustomerStatus) => Promise<Customer | null>;
  updateCustomerBalance: (
    id: string,
    amount: number,
    operation: 'add' | 'subtract' | 'set'
  ) => Promise<Customer | null>;
  searchCustomers: (query: string) => Promise<Customer[]>;
  filterCustomers: (params: {
    search?: string;
    type?: CustomerType;
    status?: CustomerStatus;
  }) => Promise<Customer[]>;
  refreshData: () => Promise<void>;
}

export const useCustomers = (): UseCustomersReturn => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [statistics, setStatistics] = useState<CustomerStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customersApi.getAll();
      setCustomers(data);
    } catch (err: any) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await customersApi.getStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Error loading customer statistics:', err);
      toast.error('Failed to load customer statistics');
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadStatistics();
  }, [loadCustomers, loadStatistics]);

  const createCustomer = useCallback(
    async (data: CreateCustomerDto): Promise<Customer | null> => {
      try {
        const newCustomer = await customersApi.create(data);
        setCustomers(prev => [newCustomer, ...prev]);
        toast.success('Customer created successfully');
        loadStatistics(); // Refresh statistics
        return newCustomer;
      } catch (err: any) {
        console.error('Error creating customer:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to create customer');
        toast.error(errorMessage);
        setError(errorMessage);
        return null;
      }
    },
    [loadStatistics]
  );

  const updateCustomer = useCallback(
    async (id: string, data: UpdateCustomerDto): Promise<Customer | null> => {
      try {
        const updatedCustomer = await customersApi.update(id, data);
        setCustomers(prev =>
          prev.map(customer => (customer.id === id ? updatedCustomer : customer))
        );
        toast.success('Customer updated successfully');
        loadStatistics(); // Refresh statistics
        return updatedCustomer;
      } catch (err: any) {
        console.error('Error updating customer:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to update customer');
        toast.error(errorMessage);
        setError(errorMessage);
        return null;
      }
    },
    [loadStatistics]
  );

  const deleteCustomer = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await customersApi.remove(id);
        setCustomers(prev => prev.filter(customer => customer.id !== id));
        toast.success('Customer deleted successfully');
        loadStatistics(); // Refresh statistics
        return true;
      } catch (err: any) {
        console.error('Error deleting customer:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to delete customer');
        toast.error(errorMessage);
        setError(errorMessage);
        return false;
      }
    },
    [loadStatistics]
  );

  const updateCustomerStatus = useCallback(
    async (id: string, status: CustomerStatus): Promise<Customer | null> => {
      try {
        const updatedCustomer = await customersApi.updateStatus(id, status);
        setCustomers(prev =>
          prev.map(customer => (customer.id === id ? updatedCustomer : customer))
        );
        toast.success('Customer status updated successfully');
        loadStatistics(); // Refresh statistics
        return updatedCustomer;
      } catch (err: any) {
        console.error('Error updating customer status:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to update customer status');
        toast.error(errorMessage);
        setError(errorMessage);
        return null;
      }
    },
    [loadStatistics]
  );

  const updateCustomerBalance = useCallback(
    async (
      id: string,
      amount: number,
      operation: 'add' | 'subtract' | 'set'
    ): Promise<Customer | null> => {
      try {
        const updatedCustomer = await customersApi.updateBalance(id, amount, operation);
        setCustomers(prev =>
          prev.map(customer => (customer.id === id ? updatedCustomer : customer))
        );
        toast.success('Customer balance updated successfully');
        loadStatistics(); // Refresh statistics
        return updatedCustomer;
      } catch (err: any) {
        console.error('Error updating customer balance:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to update customer balance');
        toast.error(errorMessage);
        setError(errorMessage);
        return null;
      }
    },
    [loadStatistics]
  );

  const searchCustomers = useCallback(
    async (query: string): Promise<Customer[]> => {
      try {
        const results = await customersApi.search(query);
        return results;
      } catch (err: any) {
        console.error('Error searching customers:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to search customers');
        toast.error(errorMessage);
        setError(errorMessage);
        return [];
      }
    },
    []
  );

  const filterCustomers = useCallback(
    async (params: {
      search?: string;
      type?: CustomerType;
      status?: CustomerStatus;
    }): Promise<Customer[]> => {
      try {
        const results = await customersApi.getFilteredCustomers(params);
        return results;
      } catch (err: any) {
        console.error('Error filtering customers:', err);
        const errorMessage = getApiErrorMessage(err, 'Failed to filter customers');
        toast.error(errorMessage);
        setError(errorMessage);
        return [];
      }
    },
    []
  );

  const refreshData = useCallback(async () => {
    await Promise.all([loadCustomers(), loadStatistics()]);
  }, [loadCustomers, loadStatistics]);

  return {
    customers,
    statistics,
    loading,
    error,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    updateCustomerStatus,
    updateCustomerBalance,
    searchCustomers,
    filterCustomers,
    refreshData,
  };
}; 